import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ElectionService, VoteService } from '../services/firebaseService';
import { Election, Vote } from '../types';
import { CheckCircleIcon, ExclamationTriangleIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';

const VotePage: React.FC = () => {
  const { member } = useAuth();
  const [elections, setElections] = useState<Election[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [electionsData, votesData] = await Promise.all([
          ElectionService.getActiveElections(),
          member ? VoteService.getVotesByMember(member.id) : []
        ]);
        
        setElections(electionsData);
        setVotes(votesData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [member]);

  const hasVoted = (electionId: string) => {
    return votes.some(vote => vote.electionId === electionId);
  };

  const getVoteForElection = (electionId: string) => {
    return votes.find(vote => vote.electionId === electionId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (elections.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune élection active</h3>
        <p className="mt-1 text-sm text-gray-500">
          Il n'y a actuellement aucune élection en cours.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Élections ASPROCOM</h1>
        <p className="mt-2 text-gray-600">
          Bonjour {member?.firstName} {member?.lastName}, voici les élections en cours.
        </p>
      </div>

      <div className="space-y-6">
        {elections.map((election) => {
          const hasVotedForThis = hasVoted(election.id);
          const userVote = getVoteForElection(election.id);

          return (
            <div key={election.id} className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {election.title}
                </h2>
                {hasVotedForThis && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Voté
                  </span>
                )}
              </div>

              <p className="text-gray-600 mb-4">{election.description}</p>

              <div className="space-y-3">
                {election.candidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className={`p-4 border rounded-lg ${
                      hasVotedForThis && userVote?.candidateId === candidate.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {candidate.name}
                        </h3>
                        {candidate.bio && (
                          <p className="text-sm text-gray-600 mt-1">
                            {candidate.bio}
                          </p>
                        )}
                      </div>
                      {hasVotedForThis && userVote?.candidateId === candidate.id && (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  </div>
                ))}

                {hasVotedForThis && userVote?.isAbstention && (
                  <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mr-2" />
                      <span className="text-yellow-800 font-medium">
                        Vous vous êtes abstenu(e) pour cette élection
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Période de vote : {election.startDate.toLocaleDateString()} - {election.endDate.toLocaleDateString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {votes.length > 0 && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">Vos votes</h3>
          <p className="text-sm text-blue-700">
            Vous avez voté pour {votes.length} élection(s). Vos choix sont enregistrés de manière sécurisée.
          </p>
        </div>
      )}

      {/* Section suivi en temps réel */}
      <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <ChartBarIcon className="w-6 h-6 mr-2 text-green-600" />
              Suivi en Temps Réel
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Suivez l'évolution des votes en direct pour toutes les élections en cours.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <ClockIcon className="w-4 h-4 mr-1" />
                <span>Mise à jour automatique</span>
              </div>
              <div className="flex items-center">
                <ChartBarIcon className="w-4 h-4 mr-1" />
                <span>Statistiques en direct</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <a
              href="/live-tracking"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium"
            >
              <ChartBarIcon className="w-5 h-5 mr-2" />
              Voir le suivi en direct
            </a>
            <p className="text-xs text-gray-500 text-center">
              S'ouvre dans un nouvel onglet
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VotePage;
